import { ComboboxProvider, Select, SelectProvider } from '@ariakit/react'
import { type FC, useCallback, useMemo, useState } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { Chip } from '@/components/commons/chip'
import { ErrorBox } from '@/components/commons/error-box'
import { AlertTriangleIcon, PlusIcon } from '@/components/ui/icons.tsx'
import { InputHelpText } from '@/components/ui/input-help-text'
import { SelectComboboxList } from '@/components/ui/select-atoms-legacy'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import type {
	ExperienceFlag,
	ExperienceFlagWithLabel
} from '@/features/experience-shell/types.ts'
import { useGetExperiencesFlags } from '@/features/experience-vetting/http/use-get-experiences-flags.ts'
import {
	OpsVettingFlagsSelectItem,
	OpsVettingFlagsSelectSearchInput,
	OpsVettingFlagsSelectStyledPopover
} from '@/features/ops-vetting/components/ops-vetting-flags-select-atoms'
import { useTranslation } from '@/translations/i18n'
import { experiencesFlagsMapToLabel } from '../utils.tsx'

const FlagsContent = ({
	isLoading,
	isError,
	flagsList,
	selectedFlagsId,
	onUpdateFlag
}: {
	isLoading: boolean
	isError: boolean
	flagsList: ExperienceFlagWithLabel[]
	selectedFlagsId: number[]
	onUpdateFlag: (checked: boolean, flag: ExperienceFlag) => void
}) => {
	const { t } = useTranslation()
	if (isLoading) {
		return (
			<div className='flex flex-col gap-1'>
				{Array.from({ length: 4 }, (_, i) => `skeleton-${i}`).map(
					(key) => (
						<OpsVettingFlagsSelectItem key={key}>
							<Skeleton height={20} width={20} />
							<Skeleton height={20} />
						</OpsVettingFlagsSelectItem>
					)
				)}
			</div>
		)
	}
	if (isError) {
		return (
			<ErrorBox
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}
	if (flagsList.length === 0) {
		return (
			<OpsVettingFlagsSelectItem>
				<InputHelpText helpText={t('common:no_results')} />
			</OpsVettingFlagsSelectItem>
		)
	}
	return flagsList.map((option) => (
		<OpsVettingFlagsSelectItem key={option.id}>
			<Checkbox
				checked={selectedFlagsId.includes(option.id)}
				id={option.id.toString()}
				onChange={(event) => onUpdateFlag(event.target.checked, option)}
			/>
			<p>{option.label}</p>
		</OpsVettingFlagsSelectItem>
	))
}

export interface ExperienceVettingCriteriaFlagsSelectProps {
	addFlag: (Flag: ExperienceFlag) => void
	removeFlag: (Flag: ExperienceFlag) => void
	selectedFlagsId: number[]
	disabled?: boolean
	scopes?: ExperienceFlag['scope'][]
}

export const ExperienceVettingCriteriaFlagsSelect: FC<
	ExperienceVettingCriteriaFlagsSelectProps
> = ({ addFlag, removeFlag, selectedFlagsId, disabled, scopes }) => {
	const { t } = useTranslation()
	const [search, setSearch] = useState('')
	const [isOpen, setIsOpen] = useState(false)
	const flagsQuery = useGetExperiencesFlags({ scopes }, { enabled: isOpen })

	const searchFlagByLabel = useCallback(
		(flags: ExperienceFlagWithLabel[], inputSearch: string) =>
			flags.filter((flag) =>
				flag.label.toLowerCase().includes(inputSearch.toLowerCase())
			),
		[]
	)

	const flagsList = useMemo(() => {
		const remappedFlags = flagsQuery.data
			? flagsQuery.data?.map((data: ExperienceFlag) => ({
					...data,
					label: t(
						experiencesFlagsMapToLabel[data.keyName] || data.keyName
					)
				}))
			: []
		return searchFlagByLabel(remappedFlags, search)
	}, [flagsQuery.data, search, searchFlagByLabel, t])

	const handleUpdateFlag = (checked: boolean, flag: ExperienceFlag) => {
		checked ? addFlag(flag) : removeFlag(flag)
	}

	return (
		<ComboboxProvider
			resetValueOnHide
			setOpen={setIsOpen}
			setValue={(search) => setSearch(search)}
			value={search}
		>
			<SelectProvider>
				<Select disabled={disabled}>
					<Chip
						isClickable
						label={'Flags'}
						startSlot={<PlusIcon />}
					/>
				</Select>
				<OpsVettingFlagsSelectStyledPopover unmountOnHide>
					<OpsVettingFlagsSelectSearchInput
						placeholder={t('photoset:search_flags')}
					/>
					<Separator orientation='horizontal' />
					<SelectComboboxList>
						<FlagsContent
							flagsList={flagsList}
							isError={flagsQuery.isError}
							isLoading={flagsQuery.isLoading}
							onUpdateFlag={handleUpdateFlag}
							selectedFlagsId={selectedFlagsId}
						/>
					</SelectComboboxList>
				</OpsVettingFlagsSelectStyledPopover>
			</SelectProvider>
		</ComboboxProvider>
	)
}
